// src/components/agenda/AgendaItem.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { renderMarkdown } from '../../services/utils';
import Task from './Task';
import TaskForm from './TaskForm';
import PollForm from './PollForm';
import PollDisplay from './PollDisplay'; 
import AgendaActionMenu from './AgendaActionMenu';
import ConfirmationModal from '../ConfirmationModal';
import { db } from '../../services/firebase'; 
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { saveTask } from '../../services/firestoreService';
import { generateId } from '../../services/utils';

import AgendaProposalModal from './AgendaProposalModal'; 

const AgendaItem = ({ 
  item, level, onUpdate, onAddSubPoint, onDeleteItem, 
  isViewMode = false, setNotification, 
  companyUsers = [],
  teams, 
  meetingId,
  currentUserId,
  companyId,         
  isModerator,       
  totalParticipants, 
  allParticipants = [], 
  isExternal = false,
  onProposeAgendaItem,
  onAssignGuest, 
  dragHandle
}) => {
  
  // === HOOKS ===
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showPollForm, setShowPollForm] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  
  const descriptionInputRef = useRef(null);
  const notesInputRef = useRef(null); 
  const [showConfirmDeleteAgendaItem, setShowConfirmDeleteAgendaItem] = useState(false);
  const [showConfirmDeleteTask, setShowConfirmDeleteTask] = useState(false);
  const [taskToDeleteId, setTaskToDeleteId] = useState(null);
  const [polls, setPolls] = useState([]);
  const [isLoadingPolls, setIsLoadingPolls] = useState(true);
  const [showProposalModal, setShowProposalModal] = useState(false);

  // Prüfen, ob der User bearbeiten darf (für UI Elemente)
  const canEditItem = (item?.createdBy === currentUserId || isModerator) && !isViewMode;

  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [isEditingDescription]);

  // Listener für Umfragen
  useEffect(() => {
    if (!companyId || !meetingId || !item?.id || isExternal) {
        setIsLoadingPolls(false);
        return;
    }
    const pollsColRef = collection(db, `companies/${companyId}/meetings/${meetingId}/polls`);
    const q = query(pollsColRef, where("agendaItemId", "==", item.id));
    setIsLoadingPolls(true);
    const unsubscribe = onSnapshot(q, (snapshot) => {
        const pollsData = snapshot.docs.map(doc => {
            const data = doc.data();
            if (!data) return null;
            const creator = Array.isArray(companyUsers) ? companyUsers.find(u => u.uid === data.createdBy) : null;
            const creatorName = creator ? `${creator.firstName || ''} ${creator.lastName || ''}`.trim() : 'Unbekannt';
            return { ...data, id: doc.id, createdByInfo: { name: creatorName } };
        }).filter(p => p !== null);
        pollsData.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        setPolls(pollsData);
        setIsLoadingPolls(false);
    }, () => setIsLoadingPolls(false));
    return () => unsubscribe();
  }, [companyId, meetingId, item?.id, companyUsers, isExternal]); 

  const creatorName = useMemo(() => {
    if (item?.creatorName) return item.creatorName;
    if (item?.createdBy && Array.isArray(companyUsers)) {
        const user = companyUsers.find(u => u.uid === item.createdBy);
        if (user) return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
    }
    return 'Unbekannter Benutzer';
  }, [item?.createdBy, item?.creatorName, companyUsers]);

  // Markdown Helper
  const insertMarkdown = (ref, type, fieldName) => {
    const textarea = ref.current;
    if (!textarea) return;
    const start = textarea.selectionStart; const end = textarea.selectionEnd; const text = textarea.value;
    const before = text.substring(0, start); const after = text.substring(end, text.length); const selection = text.substring(start, end);
    let newText = ''; let newCursorPos = start;
    if (type === 'bold') { newText = `${before}**${selection}**${after}`; newCursorPos = end + 4; if (selection.length === 0) newCursorPos = start + 2; } 
    else if (type === 'list') { const prefix = (start === 0 || text[start - 1] === '\n') ? '- ' : '\n- '; newText = `${before}${prefix}${selection}${after}`; newCursorPos = start + prefix.length + selection.length; }
    if (fieldName === 'description') handleDescriptionChange({ target: { value: newText } }); else handleNotesChange({ target: { value: newText } });
    setTimeout(() => { textarea.focus(); textarea.setSelectionRange(newCursorPos, newCursorPos); }, 0);
  };

  // Handlers
  const handleAddTask = async (taskFromForm) => { 
      if (!item?.id) return; 
      const newTaskId = taskFromForm.id || generateId(); 
      const taskToSave = { 
          ...taskFromForm, 
          id: newTaskId, 
          agendaItemId: item.id, 
          meetingId: meetingId, 
          isCompleted: taskFromForm.status === 'Erledigt' ? true : (taskFromForm.isCompleted || false), 
          status: taskFromForm.status || 'Offen', 
          creatorUserId: taskFromForm.creatorUserId || currentUserId, 
          createdAt: taskFromForm.createdAt || new Date().toISOString(), 
      }; 
      try { 
          await saveTask(taskToSave, companyId, currentUserId); 
      } catch (error) { 
          setNotification({ message: `Fehler: ${error.message}`, type: 'error' }); 
          return; 
      } 
      const updatedItem = { ...item, tasks: [...(item.tasks || []), taskToSave], }; 
      onUpdate(item.id, updatedItem); 
      setShowTaskForm(false); 
      setNotification({ message: 'Aufgabe erstellt.', type: 'info' }); 
  };

  const handleUpdateTask = async (updatedTask) => { 
      if (!item?.id) return; 
      
      const taskToSave = { 
          ...updatedTask, 
          isCompleted: updatedTask.status === 'Erledigt' ? true : (updatedTask.isCompleted || false) 
      }; 
      
      try { 
          await saveTask(taskToSave, companyId, currentUserId); 
      } catch (error) { 
          console.error("Fehler beim Speichern der Aufgabe:", error);
          setNotification({ message: `Fehler beim Speichern: ${error.message}`, type: 'error' }); 
          return;
      } 
      
      const updatedItem = { 
          ...item, 
          tasks: (item.tasks || []).map((task) => (task.id === updatedTask.id ? { ...task, ...updatedTask } : task)), 
      }; 
      
      onUpdate(item.id, updatedItem); 
      setEditingTask(null); 
      setShowTaskForm(false); 
      setNotification({ message: 'Aufgabe aktualisiert.', type: 'info' }); 
  };

  const handleDeleteTaskClick = (taskId) => { if (!canEditItem) return; setTaskToDeleteId(taskId); setShowConfirmDeleteTask(true); };
  const confirmDeleteTask = () => { if (!canEditItem || !item?.id) return; const updatedItem = { ...item, tasks: (item.tasks || []).filter(task => task.id !== taskToDeleteId) }; onUpdate(item.id, updatedItem); setShowConfirmDeleteTask(false); setTaskToDeleteId(null); setNotification({ message: 'Aufgabe entfernt.', type: 'info' }); };
  const handleDeleteAgendaItemClick = () => { if (!canEditItem) { setNotification({ message: 'Keine Berechtigung.', type: 'warning' }); return; } setShowConfirmDeleteAgendaItem(true); };
  const confirmDeleteAgendaItem = () => { if (!canEditItem || !item?.id) return; onDeleteItem(item.id, false); setShowConfirmDeleteAgendaItem(false); };
  const handleNotesChange = (e) => { if ((!canEditItem && !isViewMode) || !item?.id) return; onUpdate(item.id, { ...item, notes: e.target.value }); };
  const handleDescriptionChange = (e) => { if ((!canEditItem && !isViewMode) || !item?.id) return; onUpdate(item.id, { ...item, description: e.target.value }); };
  const handleDescriptionSave = () => { setIsEditingDescription(false); };
  const handleDescriptionKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleDescriptionSave(); } };
  const handleDescriptionClick = () => { if (canEditItem && !isViewMode) { setIsEditingDescription(true); } };
  const handleTogglePollForm = () => { if (!canEditItem) return; setShowPollForm(prev => !prev); if (showTaskForm) setShowTaskForm(false); };
  
  // *** FIX: MeetingID und CompanyID explizit übergeben ***
  const handleSubmitSubItemProposal = (description) => { 
      if (onProposeAgendaItem) {
          onProposeAgendaItem(description, meetingId, companyId); 
          setShowProposalModal(false); 
      }
  };

  if (!item) return null;

  // === SICHTBARKEITS-LOGIK ===
  const isCreator = item?.createdBy === currentUserId;
  
  if (!isModerator && !isCreator) {
      const isGlobalParticipant = allParticipants.some(p => p.uid === currentUserId || (p.id === currentUserId));
      const isAssignedGuest = item.guestUids && item.guestUids.includes(currentUserId);
      if (!isGlobalParticipant) {
          if (!isAssignedGuest) return null; 
      }
  }

  const paddingLeft = `${level * 1.5}rem`;
  const itemClasses = `bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700 ${level > 0 ? 'border-l-4 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-gray-700' : ''}`;
  const readOnlyClasses = !canEditItem ? 'bg-gray-100 dark:bg-gray-900/50 opacity-70 cursor-not-allowed' : '';
  
  const guests = item.guests || [];
  const hasGuests = guests.length > 0;

  return (
    <div style={{ paddingLeft }} className="mb-4">
      <div className={itemClasses}>
        <div className="flex-grow">
          <div className="flex items-start justify-between">
            {/* --- HAUPTINHALT (Beschreibung) --- */}
            {isEditingDescription && !isViewMode ? (
                <div className="flex-grow flex flex-col">
                    <div className="flex gap-2 mb-1">
                        <button onClick={() => insertMarkdown(descriptionInputRef, 'bold', 'description')} className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-bold hover:bg-gray-300 dark:hover:bg-gray-600" title="Fett">B</button>
                        <button onClick={() => insertMarkdown(descriptionInputRef, 'list', 'description')} className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs hover:bg-gray-300 dark:hover:bg-gray-600" title="Liste">≣</button>
                    </div>
                    <textarea ref={descriptionInputRef} value={item.description} onChange={handleDescriptionChange} onBlur={handleDescriptionSave} onKeyDown={handleDescriptionKeyDown} className={`w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-xl font-bold transition-colors duration-200 resize-y min-h-[60px] ${readOnlyClasses}`} rows="2" placeholder={`${item.number || ''} Beschreibung...`} readOnly={!canEditItem} />
                </div>
            ) : (
                <div className={`flex-grow text-xl font-bold text-gray-900 dark:text-white mb-3 prose dark:prose-invert ${!isViewMode ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400' : ''} ${!canEditItem && !isViewMode ? 'cursor-not-allowed' : ''}`} onClick={handleDescriptionClick} dangerouslySetInnerHTML={{ __html: renderMarkdown(`${item.number || ''} ${item.description || ''}`)}} />
            )}
            
            {!isViewMode && (
              <div className="flex-shrink-0 ml-4 flex items-center gap-2">
                {/* --- GAST-ICON --- */}
                {hasGuests && (
                    <div className="text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 p-1.5 rounded-lg flex items-center gap-1" title="Zusätzliche Gäste">
                        <span className="material-symbols-outlined text-sm">group_add</span>
                        <span className="text-xs font-bold">{guests.length}</span>
                    </div>
                )}
                
                {dragHandle && <div className="cursor-move text-gray-400 hover:text-gray-600 dark:text-gray-500 p-1 rounded">{dragHandle}</div>}
                
                <AgendaActionMenu
                  isModerator={isModerator}
                  canEdit={canEditItem} 
                  onAddTask={() => { setShowTaskForm(true); setEditingTask(null); setShowPollForm(false); }}
                  onAddSubPoint={onAddSubPoint}
                  onAddPoll={handleTogglePollForm} 
                  onProposeItem={() => setShowProposalModal(true)} 
                  onDeleteItem={handleDeleteAgendaItemClick}
                  onManagePermissions={() => onAssignGuest && onAssignGuest(item.id)} 
                  itemId={item.id}
                />
              </div>
            )}
          </div>
          
          {/* --- NOTIZEN --- */}
          {isViewMode ? (
            item.notes && <div className="text-gray-700 dark:text-gray-300 mt-2 text-base prose dark:prose-invert" dangerouslySetInnerHTML={{ __html: renderMarkdown(item.notes) }} />
          ) : (
            <div className="mt-2">
                {canEditItem && (
                    <div className="flex gap-2 mb-1">
                        <button onMouseDown={(e) => { e.preventDefault(); insertMarkdown(notesInputRef, 'bold', 'notes'); }} className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs font-bold hover:bg-gray-300">B</button>
                        <button onMouseDown={(e) => { e.preventDefault(); insertMarkdown(notesInputRef, 'list', 'notes'); }} className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs hover:bg-gray-300">≣</button>
                    </div>
                )}
                <textarea ref={notesInputRef} placeholder="Notizen zum Agendapunkt..." value={item.notes || ''} onChange={handleNotesChange} className={`w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-y min-h-[80px] ${readOnlyClasses}`} rows="3" readOnly={!canEditItem} />
                
                {/* --- GÄSTE ANZEIGE --- */}
                {hasGuests && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-indigo-500 uppercase flex items-center gap-1 mr-1">
                            <span className="material-symbols-outlined text-sm">person_add</span> 
                            Gäste:
                        </span>
                        {guests.map((g, idx) => (
                            <span key={g.uid || idx} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800">
                                {g.name || g.email}
                                {g.type === 'external' && <span className="ml-1 opacity-60 text-[10px]">(Extern)</span>}
                            </span>
                        ))}
                        {canEditItem && (
                            <button 
                                onClick={() => onAssignGuest && onAssignGuest(item.id)}
                                className="text-xs text-gray-400 hover:text-indigo-600 underline ml-1"
                            >
                                Bearbeiten
                            </button>
                        )}
                    </div>
                )}
            </div>
          )}

          <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Erstellt von: {creatorName}</p>
          </div>
        </div>
        
        {/* --- TASKS, POLLS, SUBPOINTS --- */}
        {((Array.isArray(item.tasks) && item.tasks.length > 0) || (showTaskForm && canEditItem)) && (
            <div className="mt-4 pt-2 border-t border-gray-100 dark:border-gray-600">
                {(item.tasks || []).map((task) => (
                    <Task 
                        key={task.id} 
                        task={task} 
                        level={level + 1} 
                        onUpdate={(t) => { if (!canEditItem) return setNotification({ message: 'Nur Moderator/Ersteller.', type: 'warning' }); setEditingTask(t); setShowTaskForm(true); }} 
                        onDelete={handleDeleteTaskClick} 
                        isViewMode={isViewMode} 
                        isOwner={canEditItem} 
                    />
                ))}
                {showTaskForm && !isViewMode && canEditItem && (
                    <TaskForm 
                        task={editingTask} 
                        onSave={editingTask ? handleUpdateTask : handleAddTask} 
                        onCancel={() => { setShowTaskForm(false); setEditingTask(null); }} 
                        companyUsers={companyUsers} 
                        teams={teams} 
                        setNotification={setNotification} 
                        agendaItemId={item.id} 
                        meetingId={meetingId} 
                        companyId={companyId} 
                        currentUserId={currentUserId} 
                    />
                )}
            </div>
        )}
        
        {((!isLoadingPolls && polls.length > 0) || (showPollForm && canEditItem)) && (
            <div className="mt-4 pt-2 border-t border-gray-100 dark:border-gray-600">
                {isLoadingPolls && <p className="text-gray-500">Lade Umfragen...</p>}
                {polls.map((poll) => (
                    <PollDisplay key={poll.id} poll={poll} companyId={companyId} meetingId={meetingId} currentUserId={currentUserId} isModerator={isModerator} totalParticipants={totalParticipants} allParticipants={allParticipants} setNotification={setNotification} />
                ))}
                {showPollForm && !isViewMode && canEditItem && (
                    <PollForm onCancel={() => setShowPollForm(false)} setNotification={setNotification} companyId={companyId} meetingId={meetingId} agendaItemId={item.id} currentUserId={currentUserId} existingPoll={null} />
                )}
            </div>
        )}
        
        {Array.isArray(item.subPoints) && item.subPoints.length > 0 && (
            <div className="mt-4 pt-2 border-t border-gray-100 dark:border-gray-600">
                {item.subPoints.map((subItem) => (
                    <AgendaItem key={subItem.id} item={subItem} level={level + 1} onUpdate={onUpdate} onAddSubPoint={onAddSubPoint} onDeleteItem={onDeleteItem} isViewMode={isViewMode} setNotification={setNotification} companyUsers={companyUsers} teams={teams} meetingId={meetingId} currentUserId={currentUserId} companyId={companyId} isModerator={isModerator} totalParticipants={totalParticipants} allParticipants={allParticipants} isExternal={isExternal} onProposeAgendaItem={onProposeAgendaItem} onAssignGuest={onAssignGuest} dragHandle={dragHandle} />
                ))}
            </div>
        )}
      </div>

      <ConfirmationModal isOpen={showConfirmDeleteTask} message="Aufgabe aus Protokoll entfernen? (Task bleibt erhalten)" onConfirm={confirmDeleteTask} onCancel={() => setShowConfirmDeleteTask(false)} />
      <ConfirmationModal isOpen={showConfirmDeleteAgendaItem} message="Punkt wirklich löschen?" onConfirm={confirmDeleteAgendaItem} onCancel={() => setShowConfirmDeleteAgendaItem(false)} />
      <AgendaProposalModal isOpen={showProposalModal} onClose={() => setShowProposalModal(false)} onSubmit={handleSubmitSubItemProposal} />
    </div>
  );
};

export default AgendaItem;